'use client';

import React from 'react';
import { FooterInfo, ContactInfo } from '@/lib/types';
import { Facebook, Mail, MapPin } from 'lucide-react';
import Link from 'next/link';

interface FooterProps {
  footer: FooterInfo;
  contact: ContactInfo;
}

export default function Footer({ footer, contact }: FooterProps) {
  return (
    <footer className="bg-navy-dark pt-24 pb-12 text-white">
      <div className="container mx-auto px-4">
        {/* Footer Grid */}
        <div className="mb-8 grid grid-cols-1 gap-16 md:grid-cols-2 lg:grid-cols-3">
          {/* Brand & Description */}
          <div className="flex flex-col gap-6">
            <Link href="/" className="flex flex-col items-start">
              <picture>
                <source
                  media="(max-width: 640px)"
                  srcSet="/images/Logo-490x200-white-color-1.mobile.webp"
                />
                <img
                  src="/images/Logo-490x200-white-color-1.webp"
                  alt="Dynamic Club"
                  width="612"
                  height="250"
                  className="h-12 w-auto object-contain md:h-16 lg:h-20"
                />
              </picture>
            </Link>
            <p className="text-sm leading-relaxed text-white/60">
              Le Conseil d&apos;administration, le secrétariat, les monitrices, les moniteurs et
              sportifs du Dynamic Club vous souhaitent la bienvenue.
            </p>
            <div className="flex gap-4">
              {contact.social.facebook && (
                <a
                  href={contact.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Consulter la page Facebook du Dynamic Club"
                  className="hover:bg-red rounded-xl bg-white/5 p-3 transition-all"
                >
                  <Facebook size={20} fill="currentColor" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="mb-8 text-lg font-black tracking-tight uppercase">Liens Rapides</h4>
            <ul className="flex flex-col gap-4">
              {footer.quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.url}
                    className="hover:text-red text-sm font-medium text-white/60 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="mb-8 text-lg font-black tracking-tight uppercase">Contact</h4>
            <div className="flex flex-col gap-6">
              <div className="flex gap-4">
                <div className="text-red flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/5">
                  <MapPin size={20} />
                </div>
                <div className="text-sm leading-relaxed font-medium text-white/60">
                  {contact.address}
                  <br />
                  {contact.city}
                  <br />
                  {contact.country}
                </div>
              </div>
              <div className="flex gap-4">
                <div className="text-red flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/5">
                  <Mail size={20} />
                </div>
                <div className="flex flex-col gap-1">
                  {contact.emails.map((email) => (
                    <a
                      key={email}
                      href={`mailto:${email}`}
                      className="hover:text-red text-sm font-medium text-white/60 transition-colors"
                    >
                      {email}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar with Partners */}
        <div className="mt-8 border-t border-white/5 pt-8">
          <div className="flex flex-col items-center gap-10 md:grid md:grid-cols-3 md:items-center">
            <div className="flex flex-col items-center gap-4 md:items-start">
              <span className="text-[10px] font-black tracking-widest text-white/20 uppercase">
                Nos Partenaires
              </span>
              <div className="flex flex-wrap justify-center gap-6 opacity-40 grayscale transition-all hover:opacity-100 hover:grayscale-0">
                {footer.partners.map((partner) => (
                  <a
                    key={partner.name}
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={partner.name}
                    className="transition-transform hover:scale-110"
                  >
                    {partner.logo ? (
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        className="h-14 w-auto object-contain"
                      />
                    ) : (
                      <span className="text-[10px] font-bold tracking-widest text-white/30 uppercase">
                        {partner.name}
                      </span>
                    )}
                  </a>
                ))}
              </div>
            </div>
            <p className="text-center text-xs font-medium text-white/30">{footer.copyright}</p>
            <div className="hidden md:block" />
          </div>
        </div>
      </div>
    </footer>
  );
}
